import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { StatCard } from '@/components/dashboard/StatCard'
import { CallVolumeChart } from '@/components/charts/CallVolumeChart'
import { RoomsChart } from '@/components/charts/RoomsChart'
import { TrendingUp, Clock, DollarSign, Sparkles } from 'lucide-react'

interface ChartDataPoint {
  date: string
  joined: number
  rooms_ended: number
}

interface Summary {
  window_days: number
  total_joins: number
  approx_minutes: number
  our_cost: number
  agora_cost: number
  hundredms_cost: number
  savings_vs_agora: number
}

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  chart_data: ChartDataPoint[]
  summary: Summary
}

export default function UsageIndex({ user, organization, chart_data, summary }: Props) {
  const hasSavings = summary.savings_vs_agora > 0

  return (
    <AppShell user={user} organization={organization}>
      <div className="space-y-8">
        <PageHeader
          eyebrow={`Usage · last ${summary.window_days} days`}
          title="Usage"
          description="Participant minutes, traffic, and your bill — calculated from real call events."
        />

        {/* Savings hero */}
        {hasSavings && (
          <div className="surface-card relative overflow-hidden p-6">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at top right, hsl(var(--signal-live) / 0.18) 0%, transparent 65%)',
              }}
            />
            <div className="relative flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--signal-live)/0.15)]">
                <Sparkles className="h-5 w-5 text-[hsl(var(--signal-live))]" />
              </div>
              <div>
                <p className="font-heading text-base font-semibold tracking-tight">
                  You saved ${summary.savings_vs_agora.toFixed(2)} vs Agora this window
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Your cost: <span className="text-foreground">${summary.our_cost.toFixed(2)}</span> · Agora would be ${summary.agora_cost.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            title="Participants Joined"
            value={summary.total_joins.toLocaleString()}
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <StatCard
            title="Approx. Minutes"
            value={summary.approx_minutes.toLocaleString()}
            description="~10 min avg per join"
            icon={<Clock className="h-4 w-4" />}
          />
          <StatCard
            title="Our Cost"
            value={`$${summary.our_cost.toFixed(2)}`}
            description="$1.00 / 1k min"
            icon={<DollarSign className="h-4 w-4" />}
          />
          <StatCard
            title="Savings vs Agora"
            value={`$${summary.savings_vs_agora.toFixed(2)}`}
            description={`Agora charges $${summary.agora_cost.toFixed(2)}`}
            icon={<Sparkles className="h-4 w-4" />}
            accent
          />
        </div>

        {/* Charts */}
        {chart_data.length === 0 ? (
          <div className="surface-card p-10 text-center text-sm text-muted-foreground">
            No usage data in the last {summary.window_days} days.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <section className="surface-card p-6">
              <h2 className="font-heading text-base font-semibold tracking-tight">
                Participants joined per day
              </h2>
              <div className="mt-4">
                <CallVolumeChart data={chart_data} />
              </div>
            </section>

            <section className="surface-card p-6">
              <h2 className="font-heading text-base font-semibold tracking-tight">
                Rooms ended per day
              </h2>
              <div className="mt-4">
                <RoomsChart data={chart_data} />
              </div>
            </section>
          </div>
        )}
      </div>
    </AppShell>
  )
}
